import { Box, Typography, Paper, Card, CardContent, Button, Chip, IconButton, Tooltip, Divider } from '@mui/material';
import { Event, CheckCircle, Warning, Visibility, Edit, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useAppSelector } from '../store/hooks';
import { Role } from '../types/user.types';
import AppLayout from '../components/layout/AppLayout';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StatusChip from '../components/common/StatusChip';
import scadenzeService from '../services/scadenzeService';
import type { Scadenza } from '../types/scadenza.types';
import { getDaysUntilExpiry, getDaysChipColor } from '../utils/dateUtils';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);

  // State per la navigazione dei mesi
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1); // 1-12
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());

  // Nomi dei mesi
  const monthNames = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
  ];

  // Fetch statistics
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['scadenze-stats'],
    queryFn: () => scadenzeService.getStats()
  });

  // Fetch scadenze del mese selezionato
  const { data: scadenzeDelMese, isLoading: scadenzeLoading } = useQuery({
    queryKey: ['scadenze-mese-corrente', selectedMonth, selectedYear],
    queryFn: () => scadenzeService.getScadenzeDelMese(selectedMonth, selectedYear)
  });

  // Funzioni per la navigazione dei mesi
  const handlePreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const getPreviousMonthName = () => {
    const prevMonth = selectedMonth === 1 ? 12 : selectedMonth - 1;
    return monthNames[prevMonth - 1];
  };

  const getNextMonthName = () => {
    const nextMonth = selectedMonth === 12 ? 1 : selectedMonth + 1;
    return monthNames[nextMonth - 1];
  };

  const statsCards = [
    {
      title: 'Scadenze Attive',
      value: stats?.totale?.attiva || 0,
      icon: <Warning sx={{ fontSize: 40, color: '#FFB84D' }} />,
      color: '#FFB84D',
      description: 'Attive',
      onClick: () => navigate('/scadenze?stato=attiva')
    },
    {
      title: 'In Scadenza',
      value: stats?.totale?.in_scadenza || 0,
      icon: <Event sx={{ fontSize: 40, color: '#FF6B35' }} />,
      color: '#FF6B35',
      description: 'In scadenza',
      onClick: () => navigate('/scadenze?stato=in_scadenza')
    },
    {
      title: 'Completate',
      value: stats?.totale?.completata || 0,
      icon: <CheckCircle sx={{ fontSize: 40, color: '#4ECDC4' }} />,
      color: '#4ECDC4',
      description: 'Rinnovate',
      onClick: () => navigate('/scadenze?stato=completata')
    }
  ];


  if (statsLoading) {
    return (
      <AppLayout>
        <LoadingSpinner message="Caricamento dashboard..." />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" color="text.secondary">
            Benvenuto, {user?.nome} {user?.cognome}
          </Typography>
        </Box>

        {/* Stats Cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 3,
            mb: 3
          }}
        >
          {statsCards.map((card, index) => (
            <Card
              key={index}
              sx={{
                height: '100%',
                borderLeft: `4px solid ${card.color}`,
                transition: 'transform 0.2s, box-shadow 0.2s',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
              onClick={card.onClick}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {card.title}
                    </Typography>
                    <Typography variant="h3" component="div" fontWeight={600}>
                      {card.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {card.description}
                    </Typography>
                  </Box>
                  <Box>{card.icon}</Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Scadenze in Gestione del Mese - Full Width List */}
        <Paper sx={{ p: { xs: 2, sm: 3 }, minHeight: 400 }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 },
            mb: 3
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                Scadenze in Gestione - {monthNames[selectedMonth - 1]} {selectedYear}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {scadenzeDelMese?.length || 0} scadenze in gestione questo mese
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, mr: { xs: 0, sm: 2 } }}>
                <Button
                  size="small"
                  startIcon={<ChevronLeft />}
                  onClick={handlePreviousMonth}
                  sx={{
                    minWidth: 'auto',
                    px: { xs: 0.5, sm: 1 },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    {getPreviousMonthName()}
                  </Box>
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  |
                </Typography>
                <Button
                  size="small"
                  endIcon={<ChevronRight />}
                  onClick={handleNextMonth}
                  sx={{
                    minWidth: 'auto',
                    px: { xs: 0.5, sm: 1 },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    {getNextMonthName()}
                  </Box>
                </Button>
              </Box>
              <Button
                size="small"
                onClick={() => navigate(`/scadenze?mese=${selectedMonth}&anno=${selectedYear}`)}
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                Vedi Tutte
              </Button>
            </Box>
          </Box>

          {scadenzeLoading ? (
            <LoadingSpinner />
          ) : !scadenzeDelMese || scadenzeDelMese.length === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px' }}>
              <Typography variant="body2" color="text.secondary">
                Nessuna scadenza in gestione per {monthNames[selectedMonth - 1]} {selectedYear}
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {scadenzeDelMese.slice(0, 20).map((scadenza: Scadenza, index: number) => {
                  const daysUntil = getDaysUntilExpiry(scadenza.dataScadenza);
                  return (
                    <Box key={scadenza.id}>
                      {index > 0 && <Divider />}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: { xs: 1, sm: 2 },
                          py: 2,
                          px: { xs: 1, sm: 2 },
                          borderLeft: '4px solid',
                          borderLeftColor: daysUntil < 0 ? 'error.main' :
                                          daysUntil === 0 ? 'error.main' :
                                          daysUntil <= 7 ? 'warning.main' :
                                          daysUntil <= 30 ? 'info.main' : 'success.main',
                          backgroundColor: daysUntil < 0 ? 'rgba(211, 47, 47, 0.04)' : 'transparent',
                          cursor: 'pointer',
                          transition: 'background-color 0.15s ease',
                          '&:hover': {
                            backgroundColor: daysUntil < 0 ? 'rgba(211, 47, 47, 0.08)' : 'action.hover'
                          }
                        }}
                        onClick={() => navigate(`/scadenze/${scadenza.id}`)}
                      >
                        {/* Client & Tipologia info */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle2" fontWeight={700} noWrap>
                            {scadenza.cliente?.cognome?.toUpperCase()} {scadenza.cliente?.nome?.toUpperCase()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {scadenza.tipologia?.nome}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Scadenza: {new Date(scadenza.dataScadenza).toLocaleDateString('it-IT')}
                          </Typography>
                        </Box>

                        {/* Status chips */}
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
                          <StatusChip stato={scadenza.stato} />
                          <Chip
                            size="small"
                            label={
                              daysUntil < 0
                                ? `Scaduto ${Math.abs(daysUntil)}gg fa`
                                : daysUntil === 0
                                ? 'Scade oggi'
                                : daysUntil <= 7
                                ? `${daysUntil} giorni`
                                : `${new Date(scadenza.dataScadenza).getDate()}/${selectedMonth}`
                            }
                            color={getDaysChipColor(daysUntil)}
                          />
                        </Box>

                        {/* Actions */}
                        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                          <Tooltip title="Visualizza">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/scadenze/${scadenza.id}`);
                              }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Modifica">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/scadenze/${scadenza.id}/edit`);
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              {scadenzeDelMese.length > 20 && (
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate(`/scadenze?mese=${selectedMonth}&anno=${selectedYear}`)}
                    startIcon={<Event />}
                  >
                    Vedi tutte le scadenze di {monthNames[selectedMonth - 1]} ({scadenzeDelMese.length})
                  </Button>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Box>
    </AppLayout>
  );
};

export default DashboardPage;
